/**
 * G010 — 氧气与水网络。
 *
 * 设计取舍见 docs/goals/G010-design-notes.md。三条决定了本文件形状的要点：
 *
 * 1. **流体可储存，电力不可**。因此结算模型与 power.ts 完全不同：这里不做
 *    「按优先级切断」，而是「净流量 → 储量增减 → 储量见底才是真短缺」，
 *    核心指标是**储备小时数**而不是当前功率余量。
 * 2. **一张管网，两种流体**。Non-goal 明写不做真实流体模拟，因此管道是导管、
 *    不区分介质；氧气与水在同一套拓扑上流动。污染于是成为**网络的属性**，
 *    这正好让「污染沿连通网络传播」有了自然落点。
 * 3. **隔离阀是管道格上的开关位**，关闭时该格在拓扑中视为不存在 —— 网络因此
 *    分裂，污染自然被挡住。不需要为「阻断」另写一套逻辑。
 *
 * 单位一律整数：氧气/水用「升」，储量用「升」，流量用「升/分钟」。
 * 浮点求和对顺序敏感，整数不敏感（与 G009 同一理由）。
 */

import { cellKey, type BuildCellId, type SpatialMapData } from '../spatial/types.js';
import { getCell } from '../spatial/grid.js';
import { neighbors } from '../spatial/ops.js';
import { MINUTES_PER_TICK } from '../kernel.js';
import {
  networkTopology, clearNetworkCache, operationalBuildings,
  type EntityTableLike, type NetworkTopology,
} from './network.js';
import type { CommandHandler, SimulationSystem } from '../system.js';
import type { SimulationContext } from '../context.js';

/* ===================== 世界状态 ===================== */

/** 流体种类。水分原水/净水两段，污水只作为副产物计入污染。 */
export type FluidKind = 'oxygen' | 'water_raw' | 'water_clean';
export const FLUID_KINDS: readonly FluidKind[] = ['oxygen', 'water_raw', 'water_clean'];

export interface FluidComponentReport {
  readonly id: string;
  /** 每种流体的产出（升/分钟）。 */
  readonly production: Readonly<Record<FluidKind, number>>;
  readonly demand: Readonly<Record<FluidKind, number>>;
  readonly stored: Readonly<Record<FluidKind, number>>;
  readonly capacity: Readonly<Record<FluidKind, number>>;
  /** 泄漏（升/分钟），由污染度推导。 */
  readonly leak: Readonly<Record<FluidKind, number>>;
  /**
   * 储备小时数：按当前净流出还能撑多久。净流入时为 -1（UI 显示「充足」）。
   * **必须由模拟层算**，不能让 UI 拿储量自己除 —— 验收要求两边数值一致。
   */
  readonly reserveHours: Readonly<Record<FluidKind, number>>;
  /** 分量平均污染度（0–10000 basis points）。 */
  readonly contamination: number;
}

export interface FluidState {
  /** 已铺管道的单元格（cellKey -> true）。 */
  pipeCells: Record<string, true>;
  /** 已关闭的隔离阀所在管道格。关闭的格在拓扑中视为不存在。 */
  closedValves: Record<string, true>;
  /** 拓扑变更计数（铺/拆管、开/关阀、建筑增删由共享层逐项比对兜底）。 */
  topologyVersion: number;
  /** 每个分量的储量（升，整数）：componentId -> kind -> 量。 */
  stored: Record<string, Record<string, number>>;
  /** 每个管道格的污染度（0–10000 basis points）。 */
  contamination: Record<string, number>;
  /** 上一 Tick 的分量报表。 */
  reports: FluidComponentReport[];
}

export function createEmptyFluidState(): FluidState {
  return {
    pipeCells: {}, closedValves: {}, topologyVersion: 0,
    stored: {}, contamination: {}, reports: [],
  };
}

export const FLUID_DOMAIN = 'fluidNetworks';
interface WorldWithFluid { [FLUID_DOMAIN]: FluidState }
function fluidOf(ctx: SimulationContext): FluidState {
  return (ctx.state as unknown as WorldWithFluid)[FLUID_DOMAIN];
}

/* ===================== 建筑投影（依赖注入） ===================== */

/** 流体所需的最小建筑定义投影。 */
export interface FluidSpec {
  readonly id: string;
  /** 每分钟产出（升/分钟），按流体种类。 */
  readonly production: Readonly<Partial<Record<FluidKind, number>>>;
  /** 每分钟需求（升/分钟）。 */
  readonly demand: Readonly<Partial<Record<FluidKind, number>>>;
  /** 储罐容量（升），按流体种类。 */
  readonly capacity: Readonly<Partial<Record<FluidKind, number>>>;
  /**
   * 单 Tick 吞吐上限（升/分钟）。净化站等转换设备用；0 表示不做转换。
   * 转换规则固定为 water_raw -> water_clean。
   */
  readonly purifyPerMinute: number;
  /** 运转时向所在分量排放的污染（basis points/分钟）。 */
  readonly contaminationPerMinute: number;
  /** 是否需要供电才能产出（断电即停产，验收第 1 条）。 */
  readonly requiresPower: boolean;
}
export type GetFluidSpec = (defId: string) => FluidSpec | undefined;

/* ===================== 稳定拒绝码 ===================== */

export type FluidRejection =
  | 'PIPE_CELL_INVALID'
  | 'PIPE_ALREADY_EXISTS'
  | 'PIPE_NOT_FOUND'
  | 'PIPE_RESOURCES_INSUFFICIENT'
  | 'VALVE_NO_PIPE'
  | 'VALVE_ALREADY_IN_STATE';

export const PIPE_COST: Readonly<Record<string, number>> = { metal: 6 };

/* ===================== 命令 ===================== */

export const LAY_PIPE = 'lay_pipe';
export const REMOVE_PIPE = 'remove_pipe';
export const SEED_PIPE = 'seed_pipe';
export const SET_VALVE = 'set_valve';

export interface PipeCellPayload { readonly cell: BuildCellId }
export interface ValvePayload { readonly cell: BuildCellId; readonly closed: boolean }

function isCellShape(c: unknown): c is BuildCellId {
  if (typeof c !== 'object' || c === null) return false;
  const o = c as Record<string, unknown>;
  return typeof o['layer'] === 'string'
    && Number.isInteger(o['ring']) && Number.isInteger(o['sector']);
}

export function createLayPipeHandler(): CommandHandler<PipeCellPayload> {
  return {
    type: LAY_PIPE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell)) {
        return { accepted: false, rejectionCode: 'PIPE_CELL_INVALID' };
      }
      if (!getCell(ctx.state.spatial, payload.cell)) {
        return { accepted: false, rejectionCode: 'PIPE_CELL_INVALID' };
      }
      const fluid = fluidOf(ctx);
      const key = cellKey(payload.cell);
      if (fluid.pipeCells[key]) return { accepted: false, rejectionCode: 'PIPE_ALREADY_EXISTS' };
      const stock = ctx.state.resources.stock as Record<string, number>;
      for (const [res, amount] of Object.entries(PIPE_COST)) {
        if ((stock[res] ?? 0) < amount) {
          return { accepted: false, rejectionCode: 'PIPE_RESOURCES_INSUFFICIENT' };
        }
      }
      for (const [res, amount] of Object.entries(PIPE_COST)) {
        stock[res] = (stock[res] ?? 0) - amount;
      }
      fluid.pipeCells[key] = true;
      fluid.topologyVersion += 1;
      ctx.emit({ type: 'PipeLaid', payload: { cell: { ...payload.cell } } });
      return { accepted: true };
    },
  };
}

/** 场景预置管道（既有殖民地的管网不是玩家铺的，不扣当期库存）。 */
export function createSeedPipeHandler(): CommandHandler<PipeCellPayload> {
  return {
    type: SEED_PIPE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell)) {
        return { accepted: false, rejectionCode: 'PIPE_CELL_INVALID' };
      }
      if (!getCell(ctx.state.spatial, payload.cell)) {
        return { accepted: false, rejectionCode: 'PIPE_CELL_INVALID' };
      }
      const fluid = fluidOf(ctx);
      const key = cellKey(payload.cell);
      if (fluid.pipeCells[key]) return { accepted: false, rejectionCode: 'PIPE_ALREADY_EXISTS' };
      fluid.pipeCells[key] = true;
      fluid.topologyVersion += 1;
      return { accepted: true };
    },
  };
}

export function createRemovePipeHandler(): CommandHandler<PipeCellPayload> {
  return {
    type: REMOVE_PIPE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell)) {
        return { accepted: false, rejectionCode: 'PIPE_CELL_INVALID' };
      }
      const fluid = fluidOf(ctx);
      const key = cellKey(payload.cell);
      if (!fluid.pipeCells[key]) return { accepted: false, rejectionCode: 'PIPE_NOT_FOUND' };
      const stock = ctx.state.resources.stock as Record<string, number>;
      for (const [res, amount] of Object.entries(PIPE_COST)) {
        stock[res] = (stock[res] ?? 0) + amount;
      }
      delete fluid.pipeCells[key];
      delete fluid.closedValves[key];
      delete fluid.contamination[key];
      fluid.topologyVersion += 1;
      ctx.emit({ type: 'PipeRemoved', payload: { cell: { ...payload.cell } } });
      return { accepted: true };
    },
  };
}

/**
 * set_valve — 开关隔离阀。
 *
 * 关闭的阀门格在拓扑计算中被视为不存在，网络因此分裂 —— 「隔离可阻断污染」
 * 不需要额外逻辑，污染本就只在分量内传播。
 */
export function createSetValveHandler(): CommandHandler<ValvePayload> {
  return {
    type: SET_VALVE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell) || typeof payload.closed !== 'boolean') {
        return { accepted: false, rejectionCode: 'PIPE_CELL_INVALID' };
      }
      const fluid = fluidOf(ctx);
      const key = cellKey(payload.cell);
      if (!fluid.pipeCells[key]) return { accepted: false, rejectionCode: 'VALVE_NO_PIPE' };
      const isClosed = fluid.closedValves[key] === true;
      if (isClosed === payload.closed) {
        return { accepted: false, rejectionCode: 'VALVE_ALREADY_IN_STATE' };
      }
      if (payload.closed) fluid.closedValves[key] = true;
      else delete fluid.closedValves[key];
      fluid.topologyVersion += 1;
      ctx.emit({
        type: payload.closed ? 'ValveClosed' : 'ValveOpened',
        payload: { cell: { ...payload.cell } },
      });
      return { accepted: true };
    },
  };
}

/* ===================== 拓扑 ===================== */

/** 参与连通的管道格 = 全部管道格 - 已关闭的阀门格。 */
function openPipeCells(fluid: FluidState): Record<string, true> {
  if (Object.keys(fluid.closedValves).length === 0) return fluid.pipeCells;
  const out: Record<string, true> = {};
  for (const k of Object.keys(fluid.pipeCells)) {
    if (!fluid.closedValves[k]) out[k] = true;
  }
  return out;
}

export function fluidTopology(
  map: SpatialMapData,
  fluid: FluidState,
  entities: EntityTableLike,
): NetworkTopology {
  return networkTopology(fluid, map, openPipeCells(fluid), entities, fluid.topologyVersion);
}

export function clearFluidTopologyCache(fluid?: FluidState): void {
  if (fluid) clearNetworkCache(fluid);
}

/* ===================== 污染传播 ===================== */

export interface FluidConfig {
  /** 每 Tick 向邻格扩散的污染比例（万分比）。 */
  readonly spreadBasisPoints: number;
  /** 污染每 Tick 的自然衰减（万分比）。 */
  readonly decayBasisPoints: number;
  /** 污染满值时的泄漏比例（万分比/分钟）—— 污染腐蚀管道。 */
  readonly leakAtFullBasisPoints: number;
}
export const DEFAULT_FLUID: FluidConfig = {
  spreadBasisPoints: 1500, decayBasisPoints: 200, leakAtFullBasisPoints: 500,
};

/**
 * 污染传播：**双缓冲**。
 *
 * 朴素写法边遍历边写邻居，会让污染在一个 Tick 内沿遍历顺序传播很远 ——
 * 传播速度取决于遍历顺序，直接违反确定性。读旧值算新值、全部算完再整体写回，
 * 传播速率因此严格是「每 Tick 一格」，与顺序无关。
 */
function spreadContamination(
  map: SpatialMapData,
  fluid: FluidState,
  cfg: FluidConfig,
): void {
  const open = openPipeCells(fluid);
  const keys = Object.keys(open).sort();
  if (keys.length === 0) return;
  const next: Record<string, number> = {};

  for (const k of keys) {
    const cur = fluid.contamination[k] ?? 0;
    const [layer, ring, sector] = k.split(':');
    const id: BuildCellId = {
      layer: layer as BuildCellId['layer'], ring: Number(ring), sector: Number(sector),
    };
    let inflow = 0, outflowShare = 0;
    for (const nb of neighbors(map, id)) {
      const nk = cellKey(nb);
      if (!open[nk]) continue;                       // 关闭的阀门格不导通
      const nv = fluid.contamination[nk] ?? 0;
      if (nv > cur) inflow += Math.floor(((nv - cur) * cfg.spreadBasisPoints) / 10000);
      else if (cur > nv) outflowShare += 1;
    }
    const outflow = outflowShare > 0
      ? Math.floor((cur * cfg.spreadBasisPoints * outflowShare) / 10000 / Math.max(1, outflowShare))
      : 0;
    const decayed = Math.floor((cur * cfg.decayBasisPoints) / 10000);
    next[k] = Math.max(0, Math.min(10000, cur + inflow - outflow - decayed));
  }
  for (const k of keys) fluid.contamination[k] = next[k]!;
}

/* ===================== 结算系统 ===================== */

const ZERO: Record<FluidKind, number> = { oxygen: 0, water_raw: 0, water_clean: 0 };
const blank = (): Record<FluidKind, number> => ({ ...ZERO });

/** 判断某实体是否因限电而停摆（读电力域的 shed 集合）。 */
interface WorldWithPowerShed { utilityNetworks?: { shed?: Record<string, true> } }
function isPowerShed(ctx: SimulationContext, entityId: string): boolean {
  const p = (ctx.state as unknown as WorldWithPowerShed).utilityNetworks;
  return p?.shed?.[entityId] === true;
}

/**
 * 流体系统。
 *
 * **必须注册在电力系统之后** —— 验收要求「断电按 Tick 顺序影响产氧」，
 * 而内核的 System Pipeline 按注册顺序执行（顺序即契约）。同一 Tick 内电力先
 * 算出谁被切断，这里再读 `power.shed` 决定产出建筑是否停摆。不走事件回调：
 * 那会让「本 Tick 还是下 Tick 生效」依赖实现细节。
 */
export function createFluidSystem(
  getSpec: GetFluidSpec,
  cfg: FluidConfig = DEFAULT_FLUID,
): SimulationSystem {
  return {
    id: 'fluid',
    update(ctx) {
      const fluid = fluidOf(ctx);
      const topo = fluidTopology(ctx.state.spatial, fluid, ctx.state.entities);
      const specOf = new Map<string, FluidSpec>();
      const shedOf = new Map<string, boolean>();
      for (const { entityId, building } of operationalBuildings(ctx.state.entities)) {
        const s = getSpec(building.defId);
        if (s) { specOf.set(entityId, s); shedOf.set(entityId, isPowerShed(ctx, entityId)); }
      }

      spreadContamination(ctx.state.spatial, fluid, cfg);

      const reports: FluidComponentReport[] = [];
      const seenComponents = new Set<string>();

      for (const comp of topo.components) {
        seenComponents.add(comp.id);
        const production = blank(), demand = blank(), capacity = blank(), leak = blank();
        let purify = 0, contamRate = 0;

        for (const eid of comp.entityIds) {
          const spec = specOf.get(eid);
          if (!spec) continue;
          // 断电即停产（验收第 1 条）；需求与储罐容量不受供电影响
          const powered = !spec.requiresPower || !shedOf.get(eid);
          if (powered) {
            for (const k of FLUID_KINDS) production[k] += spec.production[k] ?? 0;
            purify += spec.purifyPerMinute;
            contamRate += spec.contaminationPerMinute;
          }
          for (const k of FLUID_KINDS) {
            demand[k] += spec.demand[k] ?? 0;
            capacity[k] += spec.capacity[k] ?? 0;
          }
        }

        // 分量污染度 = 其管道格的平均值
        let contamSum = 0;
        for (const k of comp.conduitKeys) contamSum += fluid.contamination[k] ?? 0;
        const contamination = comp.conduitKeys.length
          ? Math.floor(contamSum / comp.conduitKeys.length) : 0;

        // 运转中的排污建筑抬高本分量污染（写回第一格，随后由传播扩散开）
        if (contamRate > 0 && comp.conduitKeys.length > 0) {
          const k0 = comp.conduitKeys[0]!;
          fluid.contamination[k0] = Math.min(10000,
            (fluid.contamination[k0] ?? 0) + contamRate * MINUTES_PER_TICK);
        }

        const store = (fluid.stored[comp.id] ??= {});
        for (const k of FLUID_KINDS) {
          // 泄漏与污染度成正比 —— 污染腐蚀管道
          const stored0 = store[k] ?? 0;
          leak[k] = Math.floor((stored0 * contamination * cfg.leakAtFullBasisPoints) / 1e8);
        }

        // 净化：原水 -> 净水，受吞吐上限与原水存量双重约束
        if (purify > 0) {
          const avail = (store['water_raw'] ?? 0) + production.water_raw * MINUTES_PER_TICK;
          const moved = Math.min(purify * MINUTES_PER_TICK, avail);
          production.water_clean += Math.floor(moved / MINUTES_PER_TICK);
          production.water_raw -= Math.floor(moved / MINUTES_PER_TICK);
        }

        const reserveHours = blank();
        for (const k of FLUID_KINDS) {
          const net = (production[k] - demand[k] - leak[k]) * MINUTES_PER_TICK;
          const cap = capacity[k];
          const cur = store[k] ?? 0;
          // 三个边界同时成立：容量上限、不得为负、单 Tick 吞吐已在上面夹过
          store[k] = Math.max(0, Math.min(cap, cur + net));
          const drain = demand[k] + leak[k] - production[k];
          reserveHours[k] = drain > 0
            ? Math.floor((store[k]! / drain) / 60 * 10) / 10   // 保留一位小数
            : -1;                                              // 净流入 = 充足
        }

        reports.push({
          id: comp.id,
          production: { ...production }, demand: { ...demand },
          stored: { oxygen: store.oxygen ?? 0, water_raw: store.water_raw ?? 0, water_clean: store.water_clean ?? 0 },
          capacity: { ...capacity }, leak: { ...leak },
          reserveHours: { ...reserveHours }, contamination,
        });
      }

      // 分量消失（拆管/关阀）后清掉它的储量，避免状态里留下永久孤儿键
      for (const id of Object.keys(fluid.stored).sort()) {
        if (!seenComponents.has(id)) delete fluid.stored[id];
      }
      fluid.reports = reports;
    },
  };
}

/* ===================== UI 投影 ===================== */

export interface FluidSummary {
  readonly production: Record<FluidKind, number>;
  readonly demand: Record<FluidKind, number>;
  readonly stored: Record<FluidKind, number>;
  readonly capacity: Record<FluidKind, number>;
  /** 全网最紧张的储备小时数（-1 = 全部充足）。 */
  readonly worstReserveHours: number;
  readonly componentCount: number;
  readonly maxContamination: number;
}

export function fluidSummary(fluid: FluidState): FluidSummary {
  const production = blank(), demand = blank(), stored = blank(), capacity = blank();
  let worst = -1, maxContam = 0;
  for (const r of fluid.reports) {
    for (const k of FLUID_KINDS) {
      production[k] += r.production[k];
      demand[k] += r.demand[k];
      stored[k] += r.stored[k];
      capacity[k] += r.capacity[k];
      const rh = r.reserveHours[k];
      if (rh >= 0 && (worst < 0 || rh < worst)) worst = rh;
    }
    if (r.contamination > maxContam) maxContam = r.contamination;
  }
  return {
    production, demand, stored, capacity,
    worstReserveHours: worst, componentCount: fluid.reports.length,
    maxContamination: maxContam,
  };
}

/** 单栋建筑的管网接入状态。 */
export function fluidStatusOf(
  fluid: FluidState,
  map: SpatialMapData,
  entities: EntityTableLike,
  entityId: string,
): { connected: boolean; componentId: string | null; contamination: number } {
  const topo = fluidTopology(map, fluid, entities);
  const componentId = topo.componentOfEntity[entityId] ?? null;
  const rep = componentId ? fluid.reports.find((r) => r.id === componentId) : undefined;
  return { connected: componentId !== null, componentId, contamination: rep?.contamination ?? 0 };
}
